import React, { useEffect, useState } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import DynamicForm from '@components/_old/dynamicForm/DynamicForm';
import { DynamicFormInputType } from '@components/_old/dynamicForm';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { inputs } from './content';
import { useMutation } from '@apollo/client';
import Loader from '@components/loader/Loader';
import { normaliseDynamicValues } from '@utils/dynamic-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import { updateEnvironmentSettings } from './queries';
import { getEnvironmentSettings } from '../queries';
import { cloneDeep, isEmpty } from 'lodash';
import DateService from '@services/DateService';
import { makeStyles } from 'tss-react/mui';
import { MAIN_THEME } from '@styles';

const useStyles = makeStyles()(() => ({
  note: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    margin: '0 0 20px 0',
  },
}));

const EnvironmentSettingsDrawer: React.FC<IEnvironmentSettingsDrawer> = ({
  open,
  minimumIssueDate,
  onSuccess,
  onClose,
}) => {
  const { classes } = useStyles();
  const [booted, setBooted] = useState<boolean>(false);

  const [inputsForm, setInputsForm] =
    useState<Record<string, DynamicFormInputType>>(inputs);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [updateEnvironmentSettingsMutation] = useMutation(
    updateEnvironmentSettings(),
    {
      refetchQueries: [getEnvironmentSettings()],
      awaitRefetchQueries: true,
    }
  );

  const [runMutation] = useMutation(getEnvironmentSettings(), {
    update(cache, { data }) {
      // Update the cache manually after the mutation
      cache.writeQuery({
        query: getEnvironmentSettings(),
        data: {
          insurance: data.insurance,
        },
      });
    },
    awaitRefetchQueries: true,
  });

  const initialize = async () => {
    try {
      const updatedInputs = cloneDeep(inputsForm);

      if (!isEmpty(minimumIssueDate)) {
        updatedInputs.minimumIssueDate.value = minimumIssueDate;
      }

      setInputsForm(updatedInputs);
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normaliseDynamicValues(inputs, values);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const mutationResult = await updateEnvironmentSettingsMutation({
        variables: {
          issueDateReference: DateService.formatDateBackend(
            data.minimumIssueDate
          ),
        },
      });

      if (!mutationResult.data.errors) {
        await runMutation();

        toast.success(
          <ToastSuccessMessage>
            {'Reference date updated successfully'}
          </ToastSuccessMessage>
        );

        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(
          <ToastErrorMessage>{getError(mutationResult.data)}</ToastErrorMessage>
        );
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <GenericDrawer
      title={'Change Reference Date'}
      onClose={() => onClose()}
      isOpen={open}
    >
      {!booted ? (
        <Loader />
      ) : (
        <>
          <DynamicForm
            inputs={inputsForm}
            onSubmit={(values) => submitForm(values)}
            buttonText={'Update'}
            submitButtonState={submitButtonState}
            disableForm={formDisabled}
            title=""
            noteSection={
              <p className={classes.note}>
                <b>Note: </b>
                Only future dates are allowed
              </p>
            }
          />
        </>
      )}
    </GenericDrawer>
  );
};

export default EnvironmentSettingsDrawer;
